import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { ConfirmDialog, ContentHeader, Field, Modal, QueryStatus, StatusBadge, Tabs, useToast } from "@cx/ui";
import type {
  ApiKeyCreated,
  ApiKeyView,
  McpProposalView,
  WebhookCreated,
  WebhookDeliveryView,
  WebhookView,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import {
  API_KEY_SCOPES,
  DEFAULT_API_KEY_DAYS,
  WEBHOOK_EVENTS,
  formatBRL,
  missingApiKeyScopes,
  webhookEventModules,
  type ApiKeyScope,
  type WebhookEvent,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { dateTime } from "../../format";
import { useSession } from "../../session";
import {
  DELIVERY_STATUS,
  DISABLED_REASON,
  EVENT_LABEL,
  EXPIRY_OPTIONS,
  KEY_STATUS,
  PROPOSAL_STATUS,
  SCOPE_LABEL,
} from "./labels";

/** Segredo mostrado uma única vez (token da chave ou segredo do webhook). */
function SecretOnce({ title, value, onClose }: { title: string; value: string | null; onClose(): void }) {
  const toast = useToast();
  return (
    <Modal
      open={value !== null}
      title={title}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Já copiei
        </button>
      }
    >
      <div className="alert alert-warning py-2">Copie agora: por segurança, ele não aparece de novo.</div>
      <div className="input-group">
        <input
          className="form-control font-monospace"
          readOnly
          value={value ?? ""}
          aria-label={title}
          data-autofocus
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => {
            void navigator.clipboard?.writeText(value ?? "");
            toast("success", "Copiado");
          }}
        >
          Copiar
        </button>
      </div>
    </Modal>
  );
}

// ---------- Chaves ----------

function KeysTab({ perms }: { perms: Permissions }) {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["api-keys", companyId];
  const list = useQuery({ queryKey: key, queryFn: () => api<{ data: ApiKeyView[] }>("/api-keys") });
  const allowed = API_KEY_SCOPES.filter((s) => missingApiKeyScopes(perms, [s]).length === 0);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [days, setDays] = useState(DEFAULT_API_KEY_DAYS);
  const [scopes, setScopes] = useState<ApiKeyScope[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [token, setToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyView | null>(null);
  const create = useMutation({
    mutationFn: () =>
      api<ApiKeyCreated>("/api-keys", {
        method: "POST",
        body: JSON.stringify({ name, scopes, expiresInDays: days }),
      }),
    onSuccess: (r) => {
      setOpen(false);
      setErrors({});
      setName("");
      setScopes([]);
      setToken(r.token);
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api<ApiKeyView>(`/api-keys/${id}/revoke`, { method: "POST" }),
    onSuccess: () => {
      toast("success", "Chave revogada");
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const canCreate = can(perms, "api_integrations", "create");
  const canUpdate = can(perms, "api_integrations", "update");
  return (
    <div className="box card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="mb-0 text-body-secondary">
          A chave dá acesso à API desta empresa com os escopos escolhidos, nunca além das suas permissões.
        </p>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
            Nova chave
          </button>
        )}
      </div>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.data && (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Prefixo</th>
                <th>Escopos</th>
                <th>Situação</th>
                <th>Expira em</th>
                <th>Último uso</th>
                <th>Criada por</th>
                <th>
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.data.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-body-secondary">
                    Nenhuma chave criada.
                  </td>
                </tr>
              )}
              {list.data.data.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="font-monospace">{k.prefix}…</td>
                  <td className="small">{k.scopes.map((s) => SCOPE_LABEL[s]).join(", ")}</td>
                  <td>
                    <StatusBadge tone={KEY_STATUS[k.status][1]}>{KEY_STATUS[k.status][0]}</StatusBadge>
                  </td>
                  <td>{dateTime(k.expiresAt)}</td>
                  <td>{k.lastUsedAt ? dateTime(k.lastUsedAt) : "Nunca"}</td>
                  <td>{k.createdBy.name}</td>
                  <td className="text-end">
                    {canUpdate && k.status === "active" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setRevoking(k)}
                      >
                        Revogar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Modal
        open={open}
        title="Nova chave de API"
        onClose={() => setOpen(false)}
        footer={
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            Criar chave
          </button>
        }
      >
        <div className="row">
          <Field label="Nome" required className="col-12" error={errors.name}>
            <input value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus />
          </Field>
          <Field label="Validade" className="col-12" error={errors.expiresInDays}>
            <select className="form-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {EXPIRY_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <fieldset className="col-12">
            <legend className="form-label fs-6">Escopos</legend>
            {errors.scopes && <div className="text-danger small mb-1">{errors.scopes}</div>}
            {allowed.map((s) => (
              <div className="form-check" key={s}>
                <input
                  id={`scope-${s}`}
                  type="checkbox"
                  className="form-check-input"
                  checked={scopes.includes(s)}
                  onChange={(e) =>
                    setScopes((x) => (e.target.checked ? [...x, s] : x.filter((y) => y !== s)))
                  }
                />
                <label className="form-check-label" htmlFor={`scope-${s}`}>
                  {SCOPE_LABEL[s]}
                </label>
              </div>
            ))}
          </fieldset>
        </div>
      </Modal>
      <SecretOnce title="Token da chave de API" value={token} onClose={() => setToken(null)} />
      <ConfirmDialog
        open={revoking !== null}
        title="Revogar chave"
        message={`A chave “${revoking?.name ?? ""}” para de funcionar na hora. Continuar?`}
        confirmLabel="Revogar"
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id);
          setRevoking(null);
        }}
        onCancel={() => setRevoking(null)}
      />
    </div>
  );
}

// ---------- Webhooks ----------

function Deliveries({ webhook, canUpdate }: { webhook: WebhookView; canUpdate: boolean }) {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["webhook-deliveries", companyId, webhook.id];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: WebhookDeliveryView[] }>(`/webhook-subscriptions/${webhook.id}/deliveries`),
    refetchInterval: 5_000,
  });
  const redeliver = useMutation({
    mutationFn: (id: string) =>
      api<WebhookDeliveryView>(`/webhook-subscriptions/${webhook.id}/deliveries/${id}/redeliver`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast("success", "Reenvio na fila");
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  return (
    <section aria-label="Log de entregas" className="mt-3">
      <h3 className="h6">Log de entregas</h3>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.data && (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Evento</th>
                <th>Situação</th>
                <th>Tentativas</th>
                <th>HTTP</th>
                <th>Detalhe</th>
                <th>
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.data.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-body-secondary">
                    Nenhuma entrega ainda.
                  </td>
                </tr>
              )}
              {list.data.data.map((d) => (
                <tr key={d.id}>
                  <td>{dateTime(d.createdAt)}</td>
                  <td className="font-monospace small">{d.event}</td>
                  <td>
                    <StatusBadge tone={DELIVERY_STATUS[d.status][1]}>
                      {DELIVERY_STATUS[d.status][0]}
                    </StatusBadge>
                  </td>
                  <td>{d.attempts}</td>
                  <td>{d.statusCode ?? "—"}</td>
                  <td className="small text-truncate" style={{ maxWidth: "16rem" }}>
                    {d.error ??
                      (d.nextAttemptAt
                        ? `Próxima tentativa ${dateTime(d.nextAttemptAt)}`
                        : (d.responseSnippet ?? ""))}
                  </td>
                  <td className="text-end">
                    {canUpdate &&
                      webhook.status === "active" &&
                      d.status !== "pending" &&
                      d.status !== "delivering" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={redeliver.isPending}
                          onClick={() => redeliver.mutate(d.id)}
                        >
                          Reenviar
                        </button>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WebhooksTab({ perms }: { perms: Permissions }) {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["webhook-subscriptions", companyId];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: WebhookView[] }>("/webhook-subscriptions"),
  });
  const allowed = WEBHOOK_EVENTS.filter((e) =>
    webhookEventModules(e).some((m) => can(perms, m as never, "read")),
  );
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [secret, setSecret] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const fail = (e: unknown) => toast("danger", errorMessage(e));
  const create = useMutation({
    mutationFn: () =>
      api<WebhookCreated>("/webhook-subscriptions", {
        method: "POST",
        body: JSON.stringify({ url, description, events }),
      }),
    onSuccess: (r) => {
      setOpen(false);
      setErrors({});
      setUrl("");
      setDescription("");
      setEvents([]);
      setSecret(r.secret);
      setSelected(r.webhook.id);
      refresh();
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      fail(e);
    },
  });
  const action = useMutation({
    mutationFn: ({ id, op }: { id: string; op: "disable" | "enable" | "rotate-secret" | "test" }) =>
      api<WebhookView | WebhookCreated | WebhookDeliveryView>(`/webhook-subscriptions/${id}/${op}`, {
        method: "POST",
      }),
    onSuccess: (r, { id, op }) => {
      if (op === "rotate-secret") setSecret((r as WebhookCreated).secret);
      if (op === "test") toast("success", "Evento de teste na fila");
      refresh();
      void qc.invalidateQueries({ queryKey: ["webhook-deliveries", companyId, id] });
    },
    onError: fail,
  });
  const canCreate = can(perms, "api_integrations", "create");
  const canUpdate = can(perms, "api_integrations", "update");
  const current = list.data?.data.find((w) => w.id === selected) ?? null;
  return (
    <div className="box card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="mb-0 text-body-secondary">
          O ERP avisa o seu sistema (POST assinado) quando algo acontece. Até 8 tentativas em cerca de 33
          horas.
        </p>
        {canCreate && (
          <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
            Nova assinatura
          </button>
        )}
      </div>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.data && (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>URL</th>
                <th>Eventos</th>
                <th>Situação</th>
                <th>Falhas seguidas</th>
                <th>
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {list.data.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-body-secondary">
                    Nenhuma assinatura.
                  </td>
                </tr>
              )}
              {list.data.data.map((w) => (
                <tr key={w.id} className={w.id === selected ? "table-active" : undefined}>
                  <td className="text-break">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start"
                      onClick={() => setSelected(w.id)}
                    >
                      {w.url}
                    </button>
                    {w.description && <div className="small text-body-secondary">{w.description}</div>}
                  </td>
                  <td className="small">{w.events.map((e) => EVENT_LABEL[e]).join(", ")}</td>
                  <td>
                    {w.status === "active" ? (
                      <StatusBadge tone="success">Ativa</StatusBadge>
                    ) : (
                      <>
                        <StatusBadge tone="danger">Desativada</StatusBadge>
                        <div className="small text-body-secondary">
                          {DISABLED_REASON[w.disabledReason ?? "manual"] ?? w.disabledReason}
                        </div>
                      </>
                    )}
                  </td>
                  <td>{w.failureStreak}</td>
                  <td className="text-end text-nowrap">
                    {canUpdate && (
                      <>
                        {w.status === "active" && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary me-1"
                            onClick={() => {
                              setSelected(w.id);
                              action.mutate({ id: w.id, op: "test" });
                            }}
                          >
                            Enviar teste
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => action.mutate({ id: w.id, op: "rotate-secret" })}
                        >
                          Girar segredo
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() =>
                            action.mutate({ id: w.id, op: w.status === "active" ? "disable" : "enable" })
                          }
                        >
                          {w.status === "active" ? "Desativar" : "Reativar"}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {current && <Deliveries webhook={current} canUpdate={canUpdate} />}
      <Modal
        open={open}
        title="Nova assinatura de webhook"
        onClose={() => setOpen(false)}
        footer={
          <button
            type="button"
            className="btn btn-primary"
            disabled={create.isPending}
            onClick={() => create.mutate()}
          >
            Criar assinatura
          </button>
        }
      >
        <div className="row">
          <Field label="URL" required className="col-12" error={errors.url} hint="https://, porta padrão">
            <input value={url} maxLength={2000} onChange={(e) => setUrl(e.target.value)} data-autofocus />
          </Field>
          <Field label="Descrição" className="col-12" error={errors.description}>
            <input value={description} maxLength={200} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <fieldset className="col-12">
            <legend className="form-label fs-6">Eventos</legend>
            {errors.events && <div className="text-danger small mb-1">{errors.events}</div>}
            {allowed.map((ev) => (
              <div className="form-check" key={ev}>
                <input
                  id={`event-${ev}`}
                  type="checkbox"
                  className="form-check-input"
                  checked={events.includes(ev)}
                  onChange={(e) =>
                    setEvents((x) => (e.target.checked ? [...x, ev] : x.filter((y) => y !== ev)))
                  }
                />
                <label className="form-check-label" htmlFor={`event-${ev}`}>
                  {EVENT_LABEL[ev]} <span className="font-monospace small text-body-secondary">{ev}</span>
                </label>
              </div>
            ))}
          </fieldset>
        </div>
      </Modal>
      <SecretOnce title="Segredo do webhook" value={secret} onClose={() => setSecret(null)} />
    </div>
  );
}

// ---------- Propostas do MCP ----------

function ProposalsTab({ perms }: { perms: Permissions }) {
  const { api, companyId } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState<McpProposalView["status"]>("pending");
  const key = ["mcp-proposals", companyId, status];
  const list = useQuery({
    queryKey: key,
    queryFn: () => api<{ data: McpProposalView[] }>(`/mcp/proposals?status=${status}`),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "accept" | "reject" }) =>
      api<McpProposalView & { salesOrderId: string | null }>(`/ai/suggestions/${id}/${decision}`, {
        method: "POST",
      }),
    onSuccess: (r) => {
      toast("success", r.salesOrderId ? "Pedido de venda criado" : "Proposta rejeitada");
      void qc.invalidateQueries({ queryKey: ["mcp-proposals", companyId] });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const canDecide = can(perms, "sales_orders", "create");
  return (
    <div className="box card p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-2">
        <p className="mb-0 text-body-secondary">
          Pedidos propostos por agentes de IA pelo MCP. Nada é criado sem a sua aprovação; vencem em 7 dias.
        </p>
        <select
          className="form-select w-auto"
          aria-label="Situação das propostas"
          value={status}
          onChange={(e) => setStatus(e.target.value as McpProposalView["status"])}
        >
          {(Object.keys(PROPOSAL_STATUS) as McpProposalView["status"][]).map((s) => (
            <option key={s} value={s}>
              {PROPOSAL_STATUS[s][0]}
            </option>
          ))}
        </select>
      </div>
      <QueryStatus loading={list.isLoading} error={list.error} />
      {list.data && list.data.data.length === 0 && (
        <p className="text-center text-body-secondary">Nenhuma proposta.</p>
      )}
      {list.data?.data.map((p) => (
        <div key={p.id} className="border rounded p-2 mb-2">
          <div className="d-flex justify-content-between gap-2">
            <div>
              <strong>{p.summary}</strong>{" "}
              <StatusBadge tone={PROPOSAL_STATUS[p.status][1]}>{PROPOSAL_STATUS[p.status][0]}</StatusBadge>
              <div className="small text-body-secondary">
                {dateTime(p.createdAt)}
                {p.rationale ? ` · ${p.rationale}` : ""}
              </div>
            </div>
            {p.status === "pending" && canDecide && (
              <div className="text-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-primary me-1"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: p.id, decision: "accept" })}
                >
                  Aprovar e criar pedido
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={decide.isPending}
                  onClick={() => decide.mutate({ id: p.id, decision: "reject" })}
                >
                  Rejeitar
                </button>
              </div>
            )}
            {p.entityId && (
              <Link className="btn btn-sm btn-link" to="/vendas/pedidos/$id" params={{ id: p.entityId }}>
                Ver pedido
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- Documentação ----------

function DocsTab() {
  const base = `${window.location.origin}/api/v1`;
  return (
    <div className="box card p-3">
      <h3 className="h6">API REST</h3>
      <p>
        Endereço: <code>{base}</code>. Envie <code>Authorization: Bearer cxk_…</code>; escritas (POST/PUT)
        exigem <code>Idempotency-Key</code>. Limite de 600 requisições por minuto por chave. Valores em
        centavos (ex.: {formatBRL(123456)} = <code>123456</code>).
      </p>
      <p>
        <a href="/api/v1/openapi.json" target="_blank" rel="noreferrer">
          Especificação OpenAPI 3.1 (openapi.json)
        </a>
      </p>
      <h3 className="h6">Webhooks</h3>
      <p>
        Cada entrega traz <code>X-CX-Signature: t=&lt;unix&gt;,v1=&lt;hex&gt;</code>, com{" "}
        <code>v1 = HMAC-SHA256(segredo, "t.corpo")</code>. Recuse se a diferença para o relógio passar de 5
        minutos e deduplique pelo <code>id</code> do evento. Responda 2xx em até 10 segundos.
      </p>
      <h3 className="h6">MCP server (Claude e outros agentes)</h3>
      <p className="mb-1">
        Endereço: <code>{base}/mcp</code> (Streamable HTTP). Use uma chave com o escopo “MCP server” e os
        módulos que o agente pode consultar. Exemplo de configuração:
      </p>
      <pre className="bg-body-tertiary p-2 rounded small">
        {JSON.stringify(
          {
            mcpServers: {
              "cx-erp": { type: "http", url: `${base}/mcp`, headers: { Authorization: "Bearer cxk_…" } },
            },
          },
          null,
          2,
        )}
      </pre>
    </div>
  );
}

/** Preferências › Integrações › API e webhooks (T3.8, RF-PRF-11). */
export function ApiPage() {
  const { me } = useSession();
  const perms = me.permissions as Permissions;
  const tabs = [
    { key: "keys", label: "Chaves", content: <KeysTab perms={perms} /> },
    { key: "webhooks", label: "Webhooks", content: <WebhooksTab perms={perms} /> },
    ...(can(perms, "sales_orders", "read")
      ? [{ key: "mcp", label: "Propostas do MCP", content: <ProposalsTab perms={perms} /> }]
      : []),
    { key: "docs", label: "Documentação", content: <DocsTab /> },
  ];
  return (
    <>
      <ContentHeader title="API e webhooks" breadcrumb={["Preferências", "Integrações", "API e webhooks"]} />
      <Tabs tabs={tabs} />
    </>
  );
}
